// 🆕 19.10.2026 · Auftrag 57 (Nachtrag 1): reine Funktionen der Klickstrecke Bodenklicks an Ankerbildern.
// Die Oberfläche (BodenklickStrecke.jsx) hält keine eigene Regel: Schalter, Punktregel (2–4, höchstens einer
// „im Lauf“), Umrechnung in die natürliche Bildgröße, nächster offener Anker und Fortschritt stehen hier.

export const PUNKTE_MIN = 2;
export const PUNKTE_MAX = 4;

// 🆕 20.10.2026 · Auftrag 67: die immer sichtbare Anleitung über dem Bild und der Zähler („n Punkte (2–4)“, damit 4 nicht als Pflicht gilt).
export const ANLEITUNG_ZEILEN = [
  "2–4 Bodenpunkte, du wählst die Spieler: möglichst stehend, über das Bild verteilt (vorn, hinten, links, rechts).",
  "Klick = Punkt dort, wo der Fuß den Boden berührt (beide Füße: Mitte).",
  "Rechtsklick auf den Punkt = entfernen.",
  "Unten je Punkt der Knopf „steht / im Lauf“; höchstens ein Punkt „im Lauf“ (roter Kreis).",
];
export const punkteZaehlerText = (n) => `${n} Punkte (${PUNKTE_MIN}–${PUNKTE_MAX})`;

export const MODUS_STRECKE = "lern";
export const MODUS_PRUEF = "pruef";

// Schalter: nur `REACT_APP_BODENKLICK_STRECKE=1` zeigt den Knopf; aus = kein Knopf, keine Anfrage.
export const schalterAn = (env = process.env) => env.REACT_APP_BODENKLICK_STRECKE === "1";

// Klickposition im angezeigten <img> → Pixel der natürlichen Bildgröße (ref_dims).
export const zuBildPixel = (clientX, clientY, rect, natW, natH) => {
  if (!rect || !rect.width || !rect.height || !natW || !natH) return null;
  const u = ((clientX - rect.left) / rect.width) * natW;
  const v = ((clientY - rect.top) / rect.height) * natH;
  if (u < 0 || v < 0 || u > natW || v > natH) return null;
  return { u: Math.round(u * 100) / 100, v: Math.round(v * 100) / 100, im_lauf: false };
};

export const punktHinzu = (punkte, p) => (p && punkte.length < PUNKTE_MAX ? [...punkte, p] : punkte);

export const punktWeg = (punkte, i) => punkte.filter((_, j) => j !== i);

// Umschalten „im Lauf“: höchstens einer; setzen nimmt den Haken bei allen anderen.
export const imLaufUmschalten = (punkte, i) =>
  punkte.map((p, j) => ({ ...p, im_lauf: j === i ? !p.im_lauf : false }));

export const punkteFehler = (punkte) => {
  if (punkte.length < PUNKTE_MIN) return `mindestens ${PUNKTE_MIN} Bodenpunkte`;
  if (punkte.length > PUNKTE_MAX) return `höchstens ${PUNKTE_MAX} Bodenpunkte`;
  if (punkte.filter((p) => p.im_lauf).length > 1) return "höchstens ein Punkt „im Lauf“";
  return null;
};

export const klickKoerper = (punkte, natW, natH) => ({
  punkte: punkte.map((p) => ({ u: p.u, v: p.v, im_lauf: !!p.im_lauf })),
  ref_dims: { w: natW, h: natH },
});

export const fertigeAnker = (klicks) => new Set((klicks || []).map((k) => k.anker_id));

// Nächster Anker der Liste ohne gespeicherte Klicks, nach dem aktuellen; am Ende von vorn; null = alle fertig.
export const naechsterOffener = (liste, klicks, aktuellId) => {
  const fertig = fertigeAnker(klicks);
  const start = Math.max(0, liste.findIndex((p) => p.anker_id === aktuellId) + 1);
  for (let k = 0; k < liste.length; k += 1) {
    const p = liste[(start + k) % liste.length];
    if (!fertig.has(p.anker_id)) return p.anker_id;
  }
  return null;
};

export const fortschrittText = (liste, klicks) => {
  const fertig = fertigeAnker(klicks);
  const n = liste.filter((p) => fertig.has(p.anker_id)).length;
  return `${n} von ${liste.length} Ankerbildern geklickt`;
};

export const zweckText = (p) => {
  if (!p) return "";
  if (p.haelfte === MODUS_PRUEF) return "Prüfhälfte (gesperrt: nie Einstellung, Training, Anker-Pfad)";
  if (p.zurueckgehalten) return `zurückgehalten (Nr. ${p.rueckhalt_nummer}, Prüfung)`;
  return "Strecke";
};

// 🆕 Auftrag 58, R4 · Anker-Prüfliste: Anzeige der Hinweise des Backends (nie Sperre).
const zahl = (x, d = 1) => (x === null || x === undefined ? "–" : Number(x).toFixed(d));

export const ankerZeile = (a) => {
  const tor = a.tor_klicks === null || a.tor_klicks === undefined ? "Tor-Klicks offen" : `Tor-Klicks ${a.tor_klicks}`;
  const rh = a.zurueckgehalten ? ` · zurückgehalten (Nr. ${a.rueckhalt_nummer})` : "";
  return `${zahl(a.t_s, 2)} s · Shot ${a.shot ?? "–"} · Linien ${a.linien} · ${tor} · Bildwinkel ${zahl(a.fov_deg)}°${rh}`;
};

export const zusammenfassungText = (z) => {
  if (!z) return "";
  const q = z.linien_ab_3_q === null || z.linien_ab_3_q === undefined ? "–" : `${Math.round(z.linien_ab_3_q)} %`;
  return `${z.anker} Anker (1. Halbzeit ${z.je_haelfte.lern}, 2. Halbzeit ${z.je_haelfte.pruef}) · ≥ 3 Linien ${z.linien_ab_3} (${q})`
    + ` · zurückgehalten ${z.zurueckgehalten}${z.ohne_rueckhalt_nummer ? ` · ${z.ohne_rueckhalt_nummer} noch ohne Nummer` : ""}`
    + ` · mit Hinweis ${z.mit_hinweis}`;
};

export const nurMitHinweis = (anker, an) => (an ? (anker || []).filter((a) => a.hinweise.length > 0) : anker || []);

// 🆕 Auftrag 60, R4 · Import-Hinweis zur Namensregel (nie Sperre). Name = Dateiname ohne Endung, wie beim Upload.
export const videoName = (dateiname) => String(dateiname || "").replace(/\.[^.]+$/, "");

export const namensHinweis = (r) => {
  if (!r) return null;
  if (r.form === "offen") {
    return "Der Name folgt keiner Form (…_Punkte_1-6 oder …_2_Halbzeit am Ende): Spiel und Halbzeit bleiben offen, "
      + "das Video zählt in der Messbank erst nach einer Umbenennung.";
  }
  return null;
};

export const vorhandenePunkte = (klicks, ankerId) => {
  const k = (klicks || []).find((x) => x.anker_id === ankerId);
  return k ? k.punkte.map((p) => ({ u: p.u, v: p.v, im_lauf: !!p.im_lauf })) : [];
};
